import { Link } from "@/i18n/LocaleLink";
import { Instagram } from "lucide-react";
import { CONTACT, useContent } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

export const Footer = () => {
  const { ui, thresholds, offerings } = useContent();
  const { lang } = useLang();

  return (
    <footer className="bg-ink text-cream/80" data-testid="footer">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 grid gap-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src="/logo-footer-full-web.png" alt="Women's Den" className="w-40 sm:w-48 h-auto mb-8 -ml-1" data-testid="footer-logo" />
          <p className="text-sm leading-relaxed text-cream/60 max-w-xs">{ui.footer.tagline}</p>
          <p className="mt-5 text-xs tracking-[0.2em] uppercase text-sand/70">{CONTACT.area}</p>
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-cream/50 mb-5">{ui.footer.thresholds}</p>
          <ul className="space-y-3">
            {thresholds.map((t) => (
              <li key={t.slug}>
                <Link to={`/thresholds/${t.slug}`} data-testid={`footer-threshold-${t.slug}`} className="text-sm hover:text-sand transition-colors duration-300">{t.name}</Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-cream/50 mb-5">{ui.footer.offerings}</p>
          <ul className="space-y-3">
            {offerings.map((o) => (
              <li key={o.slug}>
                <Link to={`/offerings/${o.slug}`} data-testid={`footer-offering-${o.slug}`} className="text-sm hover:text-sand transition-colors duration-300">{o.name}</Link>
              </li>
            ))}
            <li>
              <Link to="/womens-circle" data-testid="footer-link-circle" className="text-sm hover:text-sand transition-colors duration-300">Women's Circle</Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-cream/50 mb-5">{ui.footer.connect}</p>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" data-testid="footer-link-about" className="hover:text-sand transition-colors duration-300">{ui.nav.about}</Link></li>
            <li><Link to="/journal" data-testid="footer-link-journal" className="hover:text-sand transition-colors duration-300">{ui.nav.journal}</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-sand transition-colors duration-300">{ui.footer.contactBooking}</Link></li>
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-sand transition-colors duration-300">{ui.footer.privacy}</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-sand transition-colors duration-300">{ui.footer.terms}</Link></li>
            <li><Link to="/cancellation-refund-policy" data-testid="footer-link-cancellation" className="hover:text-sand transition-colors duration-300">{ui.footer.cancellation}</Link></li>
            <li><Link to="/withdrawal" data-testid="footer-link-withdrawal" className="hover:text-sand transition-colors duration-300">{ui.footer.withdrawal}</Link></li>
            <li>
              <button type="button" data-testid="footer-cookie-settings"
                onClick={() => window.dispatchEvent(new Event("wd:open-cookie-settings"))}
                className="hover:text-sand transition-colors duration-300">
                {lang === "nl" ? "Cookie-instellingen" : "Cookie settings"}
              </button>
            </li>
            <li><a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="hover:text-sand transition-colors duration-300">{CONTACT.email}</a></li>
            <li>
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="inline-flex items-center gap-2 hover:text-sand transition-colors duration-300">
                <Instagram size={14} strokeWidth={1.5} /> {CONTACT.instagramHandle}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between text-xs text-cream/40">
          <span>© {new Date().getFullYear()} Women's Den — Schiedam, The Netherlands</span>
          <span data-testid="footer-signal">{ui.footer.signal}</span>
          <span className="font-serif italic text-sm text-cream/50">{ui.footer.motto}</span>
        </div>
      </div>
    </footer>
  );
};
